import React from 'react';
import moment from 'moment';
import styles from './index.module.scss';

import ErrorPage from 'views/Errors/ErrorPage';
import { useMaintenanceConfig } from 'utils/hooks/useMaintenanceConfig';

// Baked-in defaults: what the app falls back to when maintenance.json can't
// be reached (or hasn't loaded on this render yet). Failing open here is the
// whole point - see useMaintenanceConfig - so these three stay in the build
// as the safety net they always were.
const {
  REACT_APP_SERVER_UP: BAKED_SERVER_UP,
  REACT_APP_SERVER_UPTIME: BAKED_UPTIME,
  REACT_APP_SERVER_MESSAGE: BAKED_MESSAGE,
} = process.env;

export default function MaintenanceBreak({ children = null }) {
  const remoteConfig = useMaintenanceConfig();
  const hostOverride = getHostOverride(remoteConfig, window.location.hostname);

  // Per-host override wins, then the fleet-wide remote value, then the
  // baked build-time default - first of those that's actually defined.
  // Ternaries, not `&&`: `hostOverride && hostOverride.serverUp` would yield
  // `null` (not `undefined`) whenever hostOverride itself is null, and
  // resolveBoolean's `typeof v !== 'undefined'` check would then treat that
  // `null` as a defined value instead of falling through.
  const isServerUp = resolveBoolean(
    hostOverride ? hostOverride.serverUp : undefined,
    remoteConfig ? remoteConfig.serverUp : undefined,
    BAKED_SERVER_UP !== 'false',
  );
  const serverUpTime =
    (hostOverride && hostOverride.uptime) ||
    (remoteConfig && remoteConfig.uptime) ||
    BAKED_UPTIME ||
    null; // null | ISO-string<"YYYY-MM-DDTHH:MMZ">
  const message =
    (hostOverride && hostOverride.message) ||
    (remoteConfig && remoteConfig.message) ||
    BAKED_MESSAGE ||
    "We're undergoing scheduled maintenance at this point.";

  const isValidUptime = moment(serverUpTime, moment.ISO_8601, true).isValid();

  if (!isServerUp && isValidUptime) {
    const now = moment();
    const uptime = moment(serverUpTime);

    let uptimeInfo = '';
    const isUptimeToday = now.isSame(uptime, 'day');
    const hasExceededTime = now.isSameOrAfter(uptime);

    if (hasExceededTime) {
      uptimeInfo = <strong>We will be back in service soon.</strong>;
    } else if (isUptimeToday) {
      const formattedUptime = uptime.format('h:mm a');
      uptimeInfo = (
        <>
          We will be back later today at <strong>{formattedUptime}</strong>.
        </>
      );
    } else {
      const formattedUptime = uptime.format('D MMMM  YYYY, h:mm a');
      uptimeInfo = (
        <>
          Please check back later on <strong>{formattedUptime}</strong>.
        </>
      );
    }

    return (
      <ErrorPage
        allowRefresh
        hideHomeButton
        buttonClassName={styles.button}
        imageContainerClassName={styles.imageContainer}
        imageClassName={styles.image}
        message={
          <div className={styles.message}>
            <p className={styles.text}>{message}</p>
            <p className={styles.text}>{uptimeInfo}</p>
          </div>
        }
      />
    );
  }

  return children;
}

function getHostOverride(config, host) {
  return (config && config.hostOverrides && config.hostOverrides[host]) || null;
}

// First defined value wins, coercing a stored "false" string the same way
// the old baked env var was compared, so a hand-edited maintenance.json
// behaves the same whether serverUp is written as a real boolean or a string.
function resolveBoolean(...values) {
  const value = values.find((v) => typeof v !== 'undefined');
  return typeof value === 'boolean' ? value : value !== 'false';
}
